/**
 * GroupBulkSettingsDialog — apply HTTP camera settings to every camera in a
 * group (item 226, superusers only).
 *
 * Two steps: choose settings (the same fields as the single-camera sheet),
 * then review a server-side preview of exactly what changes on each camera
 * and confirm. The apply carries the preview's token, so the server refuses
 * (409) if the group or its cameras' reported settings moved in between; the
 * dialog then previews again instead of applying something nobody reviewed.
 * A Status panel (task 14) shows where each camera stands; after an apply the
 * dialog stays open on it.
 */

import * as Dialog from '@radix-ui/react-dialog'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AlertTriangle, Loader2, RefreshCw, SlidersHorizontal, X } from 'lucide-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { HttpSettingsFields } from '@/components/cameras/HttpSettingsFields'
import { cameraGroupsApi } from '@/api/endpoints'
import { queryKeys } from '@/config/queryConfig'
import { classifyBulkSettingsError, type BulkSettingsError } from '@/lib/bulkSettingsError'
import { reportIsStale } from '@/lib/groupSettingsStatus'
import { GroupSettingsStatusPanel } from './GroupSettingsStatusPanel'
import {
  bulkSettingsFromFormState,
  describeBulkSettings,
  emptyFormState,
  formatSettingValue,
  settingLabel,
  type HttpSettingsFormState,
} from '@/lib/httpSettingsForm'
import { cn, formatRelativeTime } from '@/lib/utils'
import type { BulkSettings, BulkSettingsOtherRow, BulkSettingsPreview, BulkSettingsReason } from '@/types'

type Step = 'settings' | 'preview' | 'result'

interface Props {
  group: { id: number; name: string; camera_count: number }
  open: boolean
  onOpenChange: (o: boolean) => void
}

const REASON_LABELS: Record<BulkSettingsReason, string> = {
  excluded_by_request: 'Excluded by you',
  not_an_http_camera: 'Not an HTTP camera',
  already_set: 'Already has these settings',
  already_requested: 'Already queued',
}

function formatCountdown(seconds: number): string {
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`

const sameIds = (a: number[], b: number[]) => {
  const sa = new Set(a)
  return sa.size === new Set(b).size && b.every((id) => sa.has(id))
}

// A 400 naming excluded cameras that are no longer in the group (item 226 M4).
function unknownCameraIds(err: unknown): number[] | null {
  if (!isAxiosError(err) || err.response?.status !== 400) return null
  const ids = (err.response.data as { unknown_camera_ids?: unknown } | undefined)?.unknown_camera_ids
  return Array.isArray(ids) ? ids.filter((x): x is number => typeof x === 'number') : null
}

export function GroupBulkSettingsDialog({ group, open, onOpenChange }: Props) {
  const qc = useQueryClient()
  const [step, setStep] = useState<Step>('settings')
  const [form, setForm] = useState<HttpSettingsFormState>(emptyFormState)
  const set = useCallback(<K extends keyof HttpSettingsFormState>(k: K, v: HttpSettingsFormState[K]) =>
    setForm((f) => ({ ...f, [k]: v })), [])
  const settings = useMemo(() => bulkSettingsFromFormState(form), [form])
  const [excluded, setExcluded] = useState<number[]>([])
  const [preview, setPreview] = useState<BulkSettingsPreview | null>(null)
  // What the preview on screen was requested with; Apply sends exactly this.
  const [previewedWith, setPreviewedWith] = useState<{ settings: BulkSettings; exclude: number[] } | null>(null)
  // An exclusion changed since the preview on screen; Apply waits for the next one.
  const [awaitingPreview, setAwaitingPreview] = useState(false)
  const [previewError, setPreviewError] = useState<BulkSettingsError | null>(null)
  const [loading, setLoading] = useState(false)
  const [acknowledged, setAcknowledged] = useState(false)
  const [expiresAt, setExpiresAt] = useState<number | null>(null)
  const [now, setNow] = useState(() => Date.now())
  const [appliedCount, setAppliedCount] = useState(0)
  const requestSeq = useRef(0)
  const abortRef = useRef<AbortController | null>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const skipDebounceRef = useRef(false)
  const stepRef = useRef(step)
  stepRef.current = step

  const reset = () => {
    setStep('settings'); setForm(emptyFormState()); setExcluded([])
    setPreview(null); setPreviewedWith(null); setAwaitingPreview(false)
    setPreviewError(null); setAcknowledged(false); setExpiresAt(null)
  }

  useEffect(() => {
    if (open) reset()
  }, [open])  // eslint-disable-line react-hooks/exhaustive-deps

  const clearDebounce = () => {
    if (debounceRef.current) { clearTimeout(debounceRef.current); debounceRef.current = null }
  }

  // Cancel any in-flight or debounced preview: nothing still pending may move
  // the dialog back to the preview step.
  const cancelPreviews = () => {
    clearDebounce()
    abortRef.current?.abort()
    requestSeq.current++
    setLoading(false)
  }

  // Drop any in-flight or pending preview when the dialog goes away (an apply is NOT cancelled).
  useEffect(() => () => { abortRef.current?.abort(); clearDebounce() }, [])

  // Set below; lets a preview re-run itself without a circular useCallback.
  const runPreviewRef = useRef<(exclude: number[], s?: BulkSettings) => Promise<void>>(async () => {})

  // Drop excluded cameras the server says left the group and preview once more.
  // False when none of them was excluded, so a repeat 400 can never loop.
  const dropUnknownExclusions = (exclude: number[], unknown: number[], s: BulkSettings) => {
    const next = exclude.filter((id) => !unknown.includes(id))
    if (next.length === exclude.length) return false
    skipDebounceRef.current = true   // this re-preview replaces the debounced one
    setExcluded(next)
    runPreviewRef.current(next, s)
    return true
  }

  const runPreview = useCallback(async (exclude: number[], s: BulkSettings = settings) => {
    abortRef.current?.abort()
    const ctrl = new AbortController(); abortRef.current = ctrl
    const seq = ++requestSeq.current
    setLoading(true); setAcknowledged(false)
    try {
      const p = await cameraGroupsApi.previewSettings(group.id, s, exclude, ctrl.signal)
      if (seq !== requestSeq.current) return            // a newer preview superseded this one
      setPreview(p); setPreviewedWith({ settings: s, exclude }); setAwaitingPreview(false)
      setPreviewError(null); setExpiresAt(Date.now() + p.expires_in * 1000); setNow(Date.now())
      setStep('preview')
    } catch (err) {
      if (seq !== requestSeq.current) return
      const e = classifyBulkSettingsError(err)
      if (e.kind === 'aborted') return
      const unknown = unknownCameraIds(err)
      if (unknown && dropUnknownExclusions(exclude, unknown, s)) return
      // A preview is a dry run, so a lost response cannot have changed anything.
      setPreviewError(e.kind === 'network'
        ? { ...e, message: 'Could not reach the server. Nothing was changed; try again.' }
        : e)
      if (e.kind === 'invalid') setStep('settings')
      if (e.kind === 'not_found') {
        toast.error(e.message)
        qc.invalidateQueries({ queryKey: queryKeys.cameraGroups.all() })
        onOpenChange(false)
      }
    } finally {
      if (seq === requestSeq.current) setLoading(false)
    }
  }, [group.id, settings, qc, onOpenChange])  // eslint-disable-line react-hooks/exhaustive-deps
  runPreviewRef.current = runPreview

  // Debounced re-preview when exclusions change on the preview step. Back and
  // unmount cancel it, and it never fires once the step has left the preview.
  useEffect(() => {
    if (skipDebounceRef.current) { skipDebounceRef.current = false; return }
    if (step !== 'preview') return
    debounceRef.current = setTimeout(() => {
      debounceRef.current = null
      if (stepRef.current === 'preview') runPreview(excluded)
    }, 400)
    return clearDebounce
  }, [excluded])  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!expiresAt) return
    const t = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(t)
  }, [expiresAt])
  const secondsLeft = expiresAt ? Math.max(0, Math.round((expiresAt - now) / 1000)) : 0

  const apply = useMutation({
    mutationFn: () => cameraGroupsApi.applySettings(
      group.id, previewedWith!.settings, previewedWith!.exclude, preview!.preview_token),
    onSuccess: (r) => {
      toast.success(`Queued for ${plural(r.applied, 'camera')} — each applies it on its next check-in.`)
      qc.invalidateQueries({ queryKey: ['cameras'] })
      qc.invalidateQueries({ queryKey: queryKeys.cameraGroups.all() })
      qc.invalidateQueries({ queryKey: queryKeys.cameraGroups.settingsStatus(group.id) })
      cancelPreviews()
      setAppliedCount(r.applied); setStep('result')
    },
    onError: (err) => {
      const e = classifyBulkSettingsError(err)
      if (e.kind === 'stale') { toast.message(e.message); runPreview(excluded); return }
      const unknown = unknownCameraIds(err)
      if (unknown && previewedWith &&
          dropUnknownExclusions(previewedWith.exclude, unknown, previewedWith.settings)) {
        toast.message('Some cameras left the group — preview refreshed.')
        return
      }
      // Also a toast, so the outcome is seen even if the dialog is gone.
      toast.error(e.message)
      setPreviewError(e)
      if (e.kind === 'invalid') { cancelPreviews(); setStep('settings') }
    },
  })

  const toggleExcluded = (id: number) => {
    setExcluded((ex) => (ex.includes(id) ? ex.filter((x) => x !== id) : [...ex, id]))
    setAcknowledged(false); setAwaitingPreview(true)
  }

  const backToSettings = () => {
    cancelPreviews()
    setPreviewError(null); setStep('settings')
  }

  const summary = describeBulkSettings(settings)
  const nothingChosen = Object.keys(settings).length === 0
  const expired = preview !== null && secondsLeft === 0
  // The ticks on screen are not the ones the preview was made with (M2).
  const exclusionsMoved = previewedWith !== null && !sameIds(excluded, previewedWith.exclude)

  const otherList = (title: string, rows: BulkSettingsOtherRow[]) =>
    rows.length > 0 && (
      <section className="space-y-1.5">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</h4>
        <ul className="divide-y divide-border rounded-md border border-border text-sm">
          {rows.map((r) => (
            <li key={r.camera_id} className="flex items-center gap-3 px-3 py-2">
              {r.reason === 'excluded_by_request' ? (
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded accent-primary cursor-pointer"
                  checked={!excluded.includes(r.camera_id)}
                  onChange={() => toggleExcluded(r.camera_id)}
                  aria-label={`Include ${r.name}`}
                />
              ) : <span className="w-4" />}
              <span className="flex-1 truncate">{r.name}</span>
              <span className="text-xs text-muted-foreground">{REASON_LABELS[r.reason] ?? r.reason}</span>
            </li>
          ))}
        </ul>
      </section>
    )

  const errorBanner = previewError && (
    <div role="alert" className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
      <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
      <p className="flex-1">{previewError.message}</p>
      {step === 'preview' && (
        <Button size="sm" variant="outline" onClick={() => runPreview(excluded)} disabled={loading}>
          <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
          Refresh preview
        </Button>
      )}
    </div>
  )

  return (
    <Dialog.Root open={open} onOpenChange={apply.isPending ? undefined : onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 flex max-h-[90vh] w-[calc(100%-2rem)] max-w-4xl -translate-x-1/2 -translate-y-1/2 flex-col rounded-lg border border-border bg-background shadow-elevation-3">
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-border shrink-0">
            <div className="flex items-center gap-2.5 min-w-0">
              <SlidersHorizontal className="h-4 w-4 text-muted-foreground shrink-0" />
              <div className="min-w-0">
                <Dialog.Title className="text-base font-semibold truncate">Apply settings to "{group.name}"</Dialog.Title>
                <Dialog.Description className="text-xs text-muted-foreground truncate">
                  {plural(group.camera_count, 'camera')}
                </Dialog.Description>
              </div>
            </div>
            <Dialog.Close asChild>
              <Button variant="ghost" size="icon" className="shrink-0 h-8 w-8" aria-label="Close" disabled={apply.isPending}>
                <X className="h-4 w-4" />
              </Button>
            </Dialog.Close>
          </div>

          {step === 'result' ? (
            <>
              <div className="flex-1 overflow-y-auto px-5 py-5 space-y-4">
                <p className="text-sm font-medium">
                  Queued for {plural(appliedCount, 'camera')} — each applies it on its next check-in.
                </p>
                <GroupSettingsStatusPanel groupId={group.id} />
              </div>
              <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-border shrink-0">
                <Button variant="outline" onClick={reset}>Start another change</Button>
                <Dialog.Close asChild>
                  <Button>Close</Button>
                </Dialog.Close>
              </div>
            </>
          ) : step === 'settings' ? (
            <>
              <div className="flex-1 overflow-y-auto px-5 py-5 space-y-6">
                <GroupSettingsStatusPanel groupId={group.id} />
                <div className="rounded-md bg-muted/40 px-3 py-2 text-sm">
                  {summary.length === 0 ? (
                    <p className="text-muted-foreground">Choose at least one setting</p>
                  ) : (
                    <ul className="space-y-0.5">
                      {summary.map((line) => <li key={line}>{line}</li>)}
                    </ul>
                  )}
                </div>
                {errorBanner}
                <HttpSettingsFields form={form} set={set} />
              </div>
              <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-border shrink-0">
                <Dialog.Close asChild>
                  <Button variant="outline" disabled={apply.isPending}>Cancel</Button>
                </Dialog.Close>
                <Button onClick={() => runPreview(excluded)} disabled={nothingChosen || loading}>
                  {loading && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
                  Next
                </Button>
              </div>
            </>
          ) : preview && (
            <>
              <div className={cn('flex-1 overflow-y-auto px-5 py-5 space-y-5', loading && 'opacity-60')}>
                <p className="text-sm">
                  {preview.will_change} will change · {preview.already_set} already set · {preview.skipped.length} skipped
                  {loading && <Loader2 className="inline h-3.5 w-3.5 ml-2 animate-spin" />}
                </p>
                {errorBanner}

                {preview.will_change === 0 ? (
                  <p className="rounded-md bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
                    Nothing to change — every camera already has these settings
                  </p>
                ) : (
                  <div className="overflow-x-auto rounded-md border border-border">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/40 text-left text-xs text-muted-foreground">
                        <tr>
                          <th className="w-8 px-3 py-2" />
                          <th className="px-3 py-2 font-medium">Camera</th>
                          <th className="px-3 py-2 font-medium">Changes</th>
                          <th className="px-3 py-2 font-medium">Keeps</th>
                          <th className="px-3 py-2 font-medium">Last reported</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {preview.preview.map((row) => (
                          <tr key={row.camera_id} className="align-top">
                            <td className="px-3 py-2">
                              <input
                                type="checkbox"
                                className="h-4 w-4 rounded accent-primary cursor-pointer"
                                checked={!excluded.includes(row.camera_id)}
                                onChange={() => toggleExcluded(row.camera_id)}
                                aria-label={`Include ${row.name}`}
                              />
                            </td>
                            <td className="px-3 py-2 font-medium">{row.name}</td>
                            <td className="px-3 py-2">
                              {Object.entries(row.changes).map(([k, c]) => (
                                <div key={k}>
                                  {settingLabel(k)}: {formatSettingValue(k, c.reported ?? c.requested)} → {formatSettingValue(k, c.to)}
                                </div>
                              ))}
                            </td>
                            <td className="px-3 py-2 text-muted-foreground">
                              {describeBulkSettings(row.keeps as BulkSettings).map((line) => <div key={line}>{line}</div>)}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              <div className="text-muted-foreground">{formatRelativeTime(row.params_reported_at)}</div>
                              {reportIsStale(row.params_reported_at, Date.now()) && (
                                <span className="mt-1 inline-flex items-center rounded-full bg-amber-500/10 px-2 py-0.5 text-[10px] font-medium text-amber-600 dark:text-amber-400 border border-amber-500/20">
                                  Can't confirm soon
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {otherList('Already set', preview.unchanged)}
                {otherList('Skipped', preview.skipped)}
              </div>

              <div className="flex flex-wrap items-center gap-3 px-5 py-3 border-t border-border shrink-0">
                <Button variant="outline" onClick={backToSettings} disabled={apply.isPending}>Back</Button>
                {preview.will_change > 0 && (
                  <>
                    <label className="flex flex-1 items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded accent-primary cursor-pointer"
                        checked={acknowledged}
                        onChange={(e) => setAcknowledged(e.target.checked)}
                      />
                      I understand this changes {plural(preview.will_change, 'camera')}
                    </label>
                    <span className="text-xs text-muted-foreground tabular-nums">
                      {expired ? 'Preview expired' : `Preview valid for ${formatCountdown(secondsLeft)}`}
                    </span>
                    {expired ? (
                      <Button onClick={() => runPreview(excluded)} disabled={loading}>
                        <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                        Refresh preview
                      </Button>
                    ) : (
                      <Button
                        onClick={() => apply.mutate()}
                        disabled={!acknowledged || awaitingPreview || exclusionsMoved || apply.isPending || loading || secondsLeft === 0 || previewError !== null}
                      >
                        {apply.isPending && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
                        Apply
                      </Button>
                    )}
                  </>
                )}
              </div>
            </>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
